import { useEffect, useState } from 'react';
import {
  Descriptions,
  notification,
  Form,
  Space,
  Row,
  Col,
  Input,
  Typography,
  Card,
  Skeleton,
  Select,
  Transfer,
  Table,
} from 'antd';
import MESSAGES from '../../../enums/Messages';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  getModificationDocuments,
  patchRegisterDocs,
} from '../../../api/module/request_mod_documents';
import '../Form/style.scss';
import { catchException } from '../../../utils/helpers/helpers';
import { ModificationModuleInnerSaveButton } from '../../../components/ModificationComponents';
import { generateTemplateURL } from '../../../api/module/template';
import PdfViewer from '../../../components/PdfViewer';

const { Title } = Typography;

const leftTransferColumns = [
  {
    dataIndex: 'nombre_plantilla',
    title: 'Documento complementario a seleccionar',
    width: '20rem',
  },
  {
    dataIndex: 'version',
    title: 'Versión',
    align: 'center',
    width: '3rem',
  },
  {
    dataIndex: 'nombre_categoria',
    title: 'Categoría',
    width: '3rem',
  },
];

const TemplateInput = ({
  value,
  onChange,
  disabled,
  dataSource,
  pdfData,
  onUpdate,
}) => {
  const [previewLoading, setPreviewLoading] = useState();
  const [pdfURL, setPdfUrl] = useState();

  const generateTemplatePDF = async (record) => {
    try {
      setPreviewLoading(record.curr_id);
      const res = await generateTemplateURL({
        ...pdfData,
        template: record.tipo_plantilla,
        version: record.version,
      });

      setPdfUrl(res.data.url);
      setPreviewLoading();
    } catch (exception) {
      setPreviewLoading();
      catchException(
        exception,
        'No se pudo generar el PDF. Revise los nombres de las variables utilizadas.',
      );
    }
  };

  const rightTransferColumns = [
    {
      dataIndex: 'nombre_plantilla',
      title: 'Documento complementario seleccionado',
      width: '20rem',
    },
    {
      dataIndex: 'version',
      title: 'Versión',
      align: 'center',
      width: '3rem',
    },
    {
      dataIndex: 'nombre_categoria',
      title: 'Categoría',
      width: '3rem',
    },
    {
      key: 'acciones',
      title: 'Acciones',
      width: '4rem',
      render: (_, record) => (
        <Space direction={'vertical'} style={{ width: '100%' }}>
          <CustomButton
            block
            text="Ver PDF"
            type="default"
            loading={previewLoading === record.curr_id}
            onClick={() => generateTemplatePDF(record)}
            htmlType="button"
          />
          {record.up_id && (
            <CustomButton
              block
              text="Actualizar"
              htmlType="button"
              type="primary"
              className="background-green"
              onClick={async () => {
                onChange(
                  value.map((v) => (v === record.curr_id ? record.up_id : v)),
                );
                onUpdate(record.curr_id);
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Transfer
        targetKeys={value}
        showSearch
        showSelectAll={false}
        filterOption={(inputValue, item) =>
          item.nombre_plantilla
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1
        }
        rowKey={(record) => record.curr_id}
        onChange={onChange}
        disabled={disabled}
        dataSource={dataSource}
      >
        {({
          direction,
          filteredItems,
          onItemSelect,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
        }) => {
          const rowSelection = {
            getCheckboxProps: (_record) => ({
              disabled: listDisabled,
            }),
            renderCell: (_checked, record, _index, originNode) => {
              if (record.requerido && direction === 'right') return null;
              return originNode;
            },
            onSelect({ curr_id }, selected) {
              onItemSelect(curr_id, selected);
            },
            hideSelectAll: true,
            selectedRowKeys: listSelectedKeys,
          };
          return (
            <Table
              rowSelection={rowSelection}
              dataSource={filteredItems}
              rowKey="curr_id"
              style={{ pointerEvents: listDisabled ? 'none' : undefined }}
              columns={
                direction === 'left'
                  ? leftTransferColumns
                  : rightTransferColumns
              }
              pagination={false}
            />
          );
        }}
      </Transfer>
      <PdfViewer
        pdf={pdfURL}
        onCancel={() => setPdfUrl()}
        open={!!pdfURL}
        isWord
      />
    </>
  );
};

const ModificationDocuments = () => {
  var { modification_id } = useParams();
  const [form] = Form.useForm();

  const [docsTemplates, setDocsTemplates] = useState([]);
  const [dataInfo, setDataInfo] = useState();
  const [loadingFormRequest, setLoadingFormRequest] = useState(false);
  const navigate = useHistory();

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo?.proyecto_id}/${modification_id}`,
    },
    {
      text: 'Modificación Documentos Adicionales',
    },
  ];

  useEffect(() => {
    async function fetchDataInfo() {
      setDataInfo();
      try {
        const response = await getModificationDocuments(modification_id);
        setDocsTemplates(response.data.templates);
        setDataInfo(response.data);
      } catch (error) {
        catchException(error);
      }
    }
    fetchDataInfo();
  }, [modification_id]);

  const sendFormModified = async (values) => {
    setLoadingFormRequest(true);

    try {
      await patchRegisterDocs(modification_id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Módulo actualizado correctamente.',
        duration: 2,
      });
      setLoadingFormRequest(false);
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingFormRequest(false);
      catchException(error);
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN DOCUMENTOS ADICIONALES
        </Title>
      </div>
      {dataInfo ? (
        <>
          <Descriptions
            layout="horizontal"
            column={2}
            bordered
            labelStyle={{ maxWidth: '5rem' }}
          >
            <Descriptions.Item label="Contratista / Proveedor" span={2}>
              <Row align="middle">
                <Col flex="auto">{dataInfo.contratista_nombre}</Col>
                <Col flex="0 0">
                  <CustomButton
                    htmlType="button"
                    text={
                      <Link
                        to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${dataInfo.contrato_id}/${dataInfo.proyecto_id}/${dataInfo.cargo_id}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver históricos contractuales
                      </Link>
                    }
                  />
                </Col>
              </Row>
            </Descriptions.Item>
            <Descriptions.Item label="Cargo/Rol" span={2}>
              {dataInfo.cargo_nombre}
            </Descriptions.Item>
          </Descriptions>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>7. Documentos adicionales</Title>}
          >
            <p>Documentos complementarios VIGENTES</p>
            <Select
              mode="multiple"
              allowClear
              disabled
              value={dataInfo.current_documents.map(
                (item) => item.id_plantilla,
              )}
            >
              {dataInfo.current_documents.map((item) => (
                <Select.Option
                  key={`document-types-key-${item.id_plantilla}`}
                  value={item.id_plantilla}
                >
                  {item.codigo_plantilla ? `${item.codigo_plantilla} - ` : ''}
                  {item.nombre_plantilla}
                </Select.Option>
              ))}
            </Select>
          </Card>
          <Card
            size={'middle'}
            bordered={false}
            title={
              <Title level={4}>Documentos complementarios a solicitar</Title>
            }
          >
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              onFinish={sendFormModified}
              initialValues={{
                documentos_adicionales: dataInfo.docsModified,
                justificacion: dataInfo.justification,
              }}
            >
              <Form.Item
                name="documentos_adicionales"
                rules={[
                  {
                    required: true,
                    message: 'Seleccione el documento',
                  },
                ]}
              >
                <TemplateInput
                  pdfData={{
                    person: dataInfo.contratista_id,
                    term: dataInfo.termino_id,
                    contract: dataInfo.contrato_id,
                    modification: dataInfo.id,
                  }}
                  disabled={loadingFormRequest}
                  dataSource={docsTemplates}
                  onUpdate={(record_id) => {
                    setDocsTemplates((s) =>
                      s.map((v) =>
                        v.curr_id === record_id
                          ? {
                              ...v,
                              curr_id: v.up_id,
                              up_id: undefined,
                              version: v.up_version,
                              up_version: undefined,
                            }
                          : v,
                      ),
                    );
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Justificación"
                name="justificacion"
                rules={[
                  {
                    required: true,
                    message: 'Ingresa la Justificación',
                  },
                ]}
              >
                <Input.TextArea allowClear />
              </Form.Item>

              <Space>
                <CustomButton
                  htmlType="button"
                  className={'back-button'}
                  type="default"
                  text={
                    <Link
                      to={`${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`}
                    >
                      Volver
                    </Link>
                  }
                />
                <ModificationModuleInnerSaveButton
                  onClick={() => form.submit()}
                  loading={loadingFormRequest}
                />
              </Space>
            </Form>
          </Card>
        </>
      ) : (
        <Skeleton active loading />
      )}
    </>
  );
};

export default ModificationDocuments;
export { TemplateInput };
